import { NOT_SCHEDULED_DATE, orderStatusConstants } from "constants/order";
import { centsToDollars, isNoMaximumOOP, parsePrice } from "helpers/price";
import { Button } from "primereact/button";
import styled from "styled-components";

import { ROW_GAP, ROW_RADIUS } from "./styled";

// The bottom half of the row's card. The negative margin swallows the table's
// row gap so the panel butts straight against the row above, whose bottom
// border then reads as the divider between the two — hence no border-top here.
const PanelStyled = styled.div`
  margin-top: -${ROW_GAP}px;

  border: 1px solid var(--brand-gray-200);
  border-top: none;
  border-bottom-right-radius: ${ROW_RADIUS}px;
  border-bottom-left-radius: ${ROW_RADIUS}px;
  background-color: var(--brand-blue-25);
`;

const CardStyled = styled.div`
  flex: 1 1 10rem;
  min-width: 9rem;

  padding: 0.625rem 0.75rem;

  border: 1px solid var(--brand-gray-200);
  border-radius: 10px;
  background-color: var(--brand-white);
`;

const CardLabelStyled = styled.div`
  color: var(--brand-gray-600);
  font-size: 0.75rem;
  line-height: 1.2;
`;

const CardValueStyled = styled.div`
  color: var(--brand-black);
  font-size: 0.875rem;
  font-weight: var(--font-weight-semibold);
  line-height: 1.3;
`;

const CardAmountStyled = styled(CardValueStyled)`
  color: var(--brand-purple-700);
  font-size: 1.125rem;
`;

// The table carries PrimeReact's .p-fluid, whose `.p-fluid .p-button` rule
// stretches every button to the full width of its container. Full width is what
// we want stacked on a phone, but on anything wider the actions should sit
// together at their own widths.
const ActionsStyled = styled.div`
  .p-button {
    justify-content: center;
  }

  @media (min-width: 768px) {
    .p-button {
      width: auto;
    }
  }
`;

const SectionLabelStyled = styled.div`
  color: var(--brand-gray-600);
  font-size: 0.6875rem;
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

export interface IOrderRowDetailsRow {
  orderAmount: number;
  dateOfService: string;
  cptCodes: string;
  payerName: string;
  memberId: string;
  isGfe: boolean;
  status: string;
  // Present on GFE orders only.
  contractRateInCents?: number;
  deductibleInCents?: number;
  copayInCents?: number;
  coinsurancePercent?: number;
  oopRemainingInCents?: number;
}

const EMPTY_VALUE = "—";

// An order with no priced procedures has nothing to show rather than $0.00.
const formatAmount = (amountInCents?: number) =>
  amountInCents === undefined || amountInCents === null
    ? EMPTY_VALUE
    : parsePrice(centsToDollars(amountInCents));

function AmountCard({
  label,
  amountInCents,
}: {
  label: string;
  amountInCents?: number;
}) {
  return (
    <CardStyled>
      <CardLabelStyled>{label}</CardLabelStyled>
      <CardValueStyled>{formatAmount(amountInCents)}</CardValueStyled>
    </CardStyled>
  );
}

// "Cigna · ID CIG3304561", dropping whichever part the order does not carry.
const getFullInsurance = (row: IOrderRowDetailsRow) => {
  const parts = [row.payerName, row.memberId && `ID ${row.memberId}`].filter(
    Boolean
  );
  return parts.length ? parts.join(" · ") : EMPTY_VALUE;
};

export interface IOrderRowDetailsProps {
  row: IOrderRowDetailsRow;
  onCollectPayment: (row: IOrderRowDetailsRow) => void;
  onViewEstimate: (row: IOrderRowDetailsRow) => void;
  onCopyEstimateUrl: (row: IOrderRowDetailsRow) => void;
}

// The expanded body of an order row. Built to grow: the summary cards sit in a
// wrapping flex row, so later steps add cards without touching the layout.
export function OrderRowDetails({
  row,
  onCollectPayment,
  onViewEstimate,
  onCopyEstimateUrl,
}: IOrderRowDetailsProps) {
  return (
    <PanelStyled className="p-3" data-testid="order_row_details_OrderTable">
      <div className="flex flex-wrap align-items-stretch gap-2">
        <CardStyled>
          <CardLabelStyled>Patient Balance</CardLabelStyled>
          <CardAmountStyled>
            {parsePrice(centsToDollars(row.orderAmount))}
          </CardAmountStyled>
        </CardStyled>

        {row.isGfe && (
          <CardStyled data-testid="order_row_details_insurance_OrderTable">
            <CardLabelStyled>Full Insurance</CardLabelStyled>
            <CardValueStyled className="sensitive" data-pii="insurance-details">
              {getFullInsurance(row)}
            </CardValueStyled>
          </CardStyled>
        )}

        <CardStyled>
          <CardLabelStyled>CPT Codes</CardLabelStyled>
          <CardValueStyled>{row.cptCodes || EMPTY_VALUE}</CardValueStyled>
        </CardStyled>

        <CardStyled>
          <CardLabelStyled>Date of Service</CardLabelStyled>
          <CardValueStyled>
            {row.dateOfService === NOT_SCHEDULED_DATE
              ? "Not scheduled"
              : row.dateOfService || EMPTY_VALUE}
          </CardValueStyled>
        </CardStyled>
      </div>

      {row.isGfe && (
        <div className="mt-3" data-testid="order_row_breakdown_OrderTable">
          <SectionLabelStyled className="mb-2">
            Estimate Breakdown
          </SectionLabelStyled>
          <div className="flex flex-wrap align-items-stretch gap-2">
            <AmountCard
              label="Contract Rate"
              amountInCents={row.contractRateInCents}
            />
            <AmountCard
              label="Deductible"
              amountInCents={row.deductibleInCents}
            />
            <CardStyled>
              <CardLabelStyled>Coinsurance</CardLabelStyled>
              <CardValueStyled>
                {row.coinsurancePercent === undefined ||
                row.coinsurancePercent === null
                  ? EMPTY_VALUE
                  : `${row.coinsurancePercent}%`}
              </CardValueStyled>
            </CardStyled>
            <AmountCard label="Copay" amountInCents={row.copayInCents} />
            <CardStyled>
              <CardLabelStyled>OOP Remaining</CardLabelStyled>
              <CardValueStyled>
                {isNoMaximumOOP(row.oopRemainingInCents)
                  ? "No maximum"
                  : formatAmount(row.oopRemainingInCents)}
              </CardValueStyled>
            </CardStyled>
          </div>
        </div>
      )}

      <ActionsStyled className="flex flex-column md:flex-row md:flex-wrap md:align-items-center gap-2 mt-3">
        {/* Same rule as the row's own action: there is nothing to collect until
            the estimate has gone out to the patient. */}
        {row.status === orderStatusConstants.SENT_TO_PATIENT && (
          <Button
            className="p-button-sm p-button-rounded-md"
            data-testid="order_details_collect_OrderTable"
            icon="pi pi-wallet"
            label="Collect Payment"
            onClick={() => onCollectPayment(row)}
          />
        )}
        <Button
          className="p-button-sm p-button-rounded-md p-button-outlined"
          data-testid="order_details_view_OrderTable"
          icon="pi pi-arrow-up-right"
          iconPos="right"
          label="View Estimate"
          onClick={() => onViewEstimate(row)}
        />
        <Button
          className="p-button-sm p-button-rounded-md p-button-neutral"
          data-testid="order_details_copy_OrderTable"
          icon="pi pi-link"
          label="Copy Estimate Link"
          onClick={() => onCopyEstimateUrl(row)}
        />
      </ActionsStyled>
    </PanelStyled>
  );
}
